import React from 'react';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import { Button } from '../../ui/button';
import type { CsvPreview } from '../../../utils/csv-parser';
import { CsvColumnMapping } from '../../../utils/csv-parser';

interface ImportModalProps {
  csvPreview: CsvPreview | null;
  mappings: CsvColumnMapping[];
  loading: boolean;
  importProgress: string;
  onMappingsChange: React.Dispatch<React.SetStateAction<CsvColumnMapping[]>>;
  onImport: () => void;
  onCancel: () => void;
}

export const ImportModal: React.FC<ImportModalProps> = ({
  csvPreview,
  mappings,
  loading,
  importProgress,
  onMappingsChange,
  onImport,
  onCancel,
}) => {
  if (!csvPreview) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-2 sm:mx-0 max-h-[80vh] overflow-hidden">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Import CSV Preview</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {csvPreview.totalRows} records found. Showing first 10 rows.
          </p>
        </div>

        <div className="p-3 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 180px)' }}>
          {/* Column Mappings */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Column Mappings</h4>
            <div className="space-y-2">
              {mappings.map((mapping, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-4">
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 w-20 sm:w-32 flex-shrink-0">{mapping.targetField}:</span>
                  <select
                    value={mapping.csvColumn}
                    onChange={(e) => {
                      const newMappings = [...mappings];
                      newMappings[idx] = { ...mapping, csvColumn: e.target.value };
                      onMappingsChange(newMappings);
                    }}
                    className="flex-1 px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Column --</option>
                    {csvPreview.headers.map((header: string) => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  {csvPreview.headers.map((header: string, idx: number) => (
                    <th key={idx} className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {csvPreview.rows.map((row: string[], rowIdx: number) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-900 dark:text-white">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          {importProgress && (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{importProgress}</span>
            </div>
          )}
          {!importProgress && <div />}

          <div className="flex space-x-3">
            <Button
              onClick={onCancel}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={onImport}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading || !mappings.some(m => m.targetField === 'email' && m.csvColumn)}
            >
              Import
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
